import React, { useCallback, useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import ResolveImage from 'components/ResolveImage';
import useCallbackRef from 'hooks/useCallbackRef';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import { EnterpriseMember, UpdateEnterpriseMember } from 'models/Enterprises';
import { EditMemberDrawer } from './components/EditMemberDrawer';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import AuthContext from 'providers/Auth/Auth.context';
import api from 'api';
import { useNavigate } from 'react-router-dom';
import useQueryParams from 'hooks/useQueryParams';
import ProgressBar from 'components/ProgressBar';
import MemberInfo from './components/MemberInfo';
import { BUSINESS_ROUTE, SELLING_PORTFOLIO_ROUTE } from 'data/global';
import MemberPassword from './components/MemberPassword';
import useProgressTimer from 'hooks/useProgressTimer';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import mixpanelService from 'services/mixpanelService';
import { SIGNUP_SOURCE, SignupInitiatedProps } from 'models/MixpanelEvents';

import './OnboardEnterpriseMember.styles.scss';
import './OnboardEnterpriseMember.styles.responsive.scss';

type OnboardEnterpriseMemberProps = {
  className?: string;
};

const OnboardEnterpriseMember: React.FC<OnboardEnterpriseMemberProps> = (
  props,
) => {
  const { className } = props;

  const [isFirstStep, setIsFirstStep] = useState(true);
  const [isFetchingMember, setIsFetchingMember] = useState(false);
  const [member, setMember] = useState<Partial<EnterpriseMember>>();

  const logo = member?.enterprise?.logo?.url;
  const { progress, isInProgress, startTimer } = useProgressTimer();

  const isLoading = isFetchingMember || isInProgress || !member;

  const {
    params: { token },
  } = useQueryParams<{ token: string }>();

  const { popServerError } = useContext(PopNotificationsContext);
  const { onLoginReq } = useContext(AuthContext);

  const [editMemberDrawer, editMemberDrawerRef] =
    useCallbackRef<SideDrawerRef>();

  const classes = classNames('onboard-enterprise-member', className);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const getMixpanelProps = useGetMixpanelProps();

  const updateImage = useCallback(
    (profileImage) => setMember((prev) => ({ ...prev, profileImage })),
    [],
  );

  const onEditMemberSuccess = useCallback(
    (editMemberValues: Partial<UpdateEnterpriseMember>) => {
      const { firstName, lastName, title, phone, linkedinAccount } =
        editMemberValues;

      setMember((prev) => ({
        ...prev,
        firstName,
        lastName,
        title,
        user: {
          ...prev.user,
          linkedinAccount: linkedinAccount || undefined,
          phone: phone || undefined,
        },
      }));
    },
    [],
  );

  const onSubmit = useCallback(
    async (values: { password: string }) => {
      const { password } = values;
      const { firstName, lastName, title, user } = member;

      const val = {
        password,
        firstName,
        lastName,
        title,
        linkedinAccount: user?.linkedinAccount || undefined,
        phone: user?.phone || undefined,
      };

      try {
        mixpanelService.signupInitiated(
          getMixpanelProps({
            socialSignup: false,
            signupSource: SIGNUP_SOURCE.ENTERPRISE_INVITATION,
          }) as SignupInitiatedProps,
        );

        const { data } = await api.onboardEnterpiseMember.publishMember(
          token,
          val,
        );

        onLoginReq(data, SELLING_PORTFOLIO_ROUTE);
      } catch (e) {
        popServerError(e);
      }
    },
    [member, token, getMixpanelProps, onLoginReq, popServerError],
  );

  useEffect(() => {
    (async () => {
      try {
        setIsFetchingMember(true);
        startTimer();
        const { data } = await api.onboardEnterpiseMember.getMember(token);
        setMember(data);
      } catch (e) {
        navigate(BUSINESS_ROUTE, { replace: true });
      } finally {
        setIsFetchingMember(false);
      }
    })();
  }, [navigate, startTimer, token]);

  return (
    <div className={classes}>
      {logo && !isLoading ? (
        <ResolveImage src={logo} />
      ) : (
        <span className="logo-placeholder" />
      )}

      {isLoading ? (
        <div className="onboard-enterprise-member--loading">
          <h1>{t('welcome')}</h1>

          <ProgressBar percentage={progress} />
        </div>
      ) : (
        <div className="onboard-enterprise-member__wrapper">
          <MemberInfo
            className={classNames({ active: isFirstStep })}
            member={member}
            onEdit={() => editMemberDrawer?.setOpen(true)}
            onNext={() => setIsFirstStep(false)}
          />

          <MemberPassword
            className={classNames({ active: !isFirstStep })}
            onBack={() => setIsFirstStep(true)}
            onSubmit={onSubmit}
          />
        </div>
      )}

      <EditMemberDrawer
        member={member}
        ref={editMemberDrawerRef}
        onSuccess={onEditMemberSuccess}
        updateImage={(profileImage) => updateImage(profileImage)}
      />
    </div>
  );
};

export default OnboardEnterpriseMember;
